export const MAX_IMPORT_FILE_SIZE_BYTES = 10 * 1024 * 1024

export const PROVIDER_IMPORT_FIELDS = {
  externalCode: {
    key: 'externalCode',
    label: 'Код постачальника',
    required: true,
    aliases: ['externalcode', 'external code', 'external_code', 'код', 'код постачальника', 'код поставщика', 'зовнішній код', 'внешний код', 'provider code', 'supplier code'],
  },
  name: {
    key: 'name',
    label: 'Назва',
    required: true,
    aliases: ['name', 'назва', 'название', 'найменування', 'наименование', 'постачальник', 'поставщик', 'provider', 'supplier'],
  },
  markup: {
    key: 'markup',
    label: 'Націнка',
    required: false,
    aliases: ['markup', 'націнка', 'наценка', 'margin'],
  },
  exchangeRate: {
    key: 'exchangeRate',
    label: 'Курс',
    required: false,
    aliases: ['exchangerate', 'exchange rate', 'exchange_rate', 'курс', 'курс валюти', 'курс валюты', 'rate'],
  },
}

export const PROVIDER_IMPORT_FIELD_LIST = Object.values(PROVIDER_IMPORT_FIELDS)

export function getImportFileExtension(fileName = '') {
  const parts = String(fileName).toLowerCase().split('.')
  return parts.length > 1 ? parts.pop() : ''
}

export function validateImportFile(file) {
  if (!file) {
    return {
      valid: false,
      message: 'Оберіть файл для імпорту.',
    }
  }

  const extension = getImportFileExtension(file.name)
  if (!['csv', 'xlsx'].includes(extension)) {
    return {
      valid: false,
      message: 'Підтримуються тільки CSV або XLSX файли.',
    }
  }

  if (Number(file.size) > MAX_IMPORT_FILE_SIZE_BYTES) {
    return {
      valid: false,
      message: 'Розмір файлу має бути не більше 10 MB.',
    }
  }

  return {
    valid: true,
    message: '',
  }
}

function countDelimiterOutsideQuotes(row, delimiter) {
  let count = 0
  let insideQuotes = false

  for (let index = 0; index < row.length; index += 1) {
    const char = row[index]
    const nextChar = row[index + 1]

    if (char === '"' && insideQuotes && nextChar === '"') {
      index += 1
      continue
    }

    if (char === '"') {
      insideQuotes = !insideQuotes
      continue
    }

    if (char === delimiter && !insideQuotes) {
      count += 1
    }
  }

  return count
}

function detectCsvDelimiter(row) {
  return [',', ';', '\t'].reduce((selected, delimiter) => {
    const selectedCount = countDelimiterOutsideQuotes(row, selected)
    const delimiterCount = countDelimiterOutsideQuotes(row, delimiter)
    return delimiterCount > selectedCount ? delimiter : selected
  }, ',')
}

function parseDelimitedRow(row, delimiter) {
  const values = []
  let value = ''
  let insideQuotes = false

  for (let index = 0; index < row.length; index += 1) {
    const char = row[index]
    const nextChar = row[index + 1]

    if (char === '"' && insideQuotes && nextChar === '"') {
      value += '"'
      index += 1
      continue
    }

    if (char === '"') {
      insideQuotes = !insideQuotes
      continue
    }

    if (char === delimiter && !insideQuotes) {
      values.push(value.trim())
      value = ''
      continue
    }

    value += char
  }

  values.push(value.trim())
  return values.filter((header) => header !== '')
}

export function parseCsvHeaders(text) {
  const firstRow = String(text)
    .split(/\r?\n/)
    .find((row) => row.trim() !== '') || ''

  if (!firstRow) {
    return []
  }

  return parseDelimitedRow(firstRow, detectCsvDelimiter(firstRow))
}

function normalizeHeader(value) {
  return String(value)
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[\s_.\-:;,'"()[\]{}]+/g, '')
    .trim()
}

function findHeaderMatch(headers, aliases, usedHeaders) {
  const normalizedAliases = aliases.map(normalizeHeader)

  return headers.find((header) => {
    if (usedHeaders.has(header)) {
      return false
    }

    const normalizedHeader = normalizeHeader(header)
    return normalizedAliases.some((alias) => normalizedHeader === alias)
  }) || ''
}

export function guessProviderImportMapping(headers = []) {
  const usedHeaders = new Set()

  return PROVIDER_IMPORT_FIELD_LIST.reduce((mapping, field) => {
    const matchedHeader = findHeaderMatch(headers, field.aliases, usedHeaders)

    mapping[field.key] = matchedHeader
    if (matchedHeader) {
      usedHeaders.add(matchedHeader)
    }

    return mapping
  }, {})
}

export function isProviderImportMappingComplete(mapping = {}) {
  return PROVIDER_IMPORT_FIELD_LIST
    .filter((field) => field.required)
    .every((field) => String(mapping[field.key] || '').trim() !== '')
}

export function createProvidersImportFormData(file, mapping) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('mapping', JSON.stringify(mapping))
  return formData
}

export function isProviderImportConfirmAllowed(preview, isSubmitting) {
  return Boolean(
    !isSubmitting &&
    preview?.sessionId &&
    preview?.status === 'ready' &&
    Number(preview?.summary?.errorCount || 0) === 0
  )
}

function getFirstXlsxHeaderRow(rows) {
  const firstRow = rows.find((row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== ''))

  if (!firstRow) {
    return []
  }

  return firstRow
    .map((cell) => String(cell ?? '').trim())
    .filter((cell) => cell !== '')
}

export async function readImportHeadersFromFile(file) {
  const validation = validateImportFile(file)
  if (!validation.valid) {
    throw new Error(validation.message)
  }

  const extension = getImportFileExtension(file.name)

  if (extension === 'csv') {
    return parseCsvHeaders(await file.text())
  }

  const XLSX = await import('xlsx')
  const workbook = XLSX.read(await file.arrayBuffer(), {
    type: 'array',
    cellText: true,
    cellDates: false,
  })
  const firstSheetName = workbook.SheetNames[0]

  if (!firstSheetName) {
    return []
  }

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    raw: false,
    defval: '',
    blankrows: false,
  })

  return getFirstXlsxHeaderRow(rows)
}
